import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ImagePlus, X, Loader2, Swords, Shield, Disc3, PenSquare, Users } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EVENT_KINDS = [
  { value: "raid", label: "Raid", icon: Swords },
  { value: "game", label: "Campaign", icon: Shield },
  { value: "game_show", label: "Game Show", icon: Disc3 },
];

const LFG_KINDS = [
  { value: "looking_for_players", label: "Looking for Players" },
  { value: "looking_for_table", label: "Looking for a Table" },
];

const LFG_SYSTEMS = [
  { value: "any", label: "Any System" },
  { value: "pf2e", label: "Pathfinder 2e" },
  { value: "dnd5e", label: "D&D 5e" },
  { value: "homebrew", label: "Homebrew" },
];

const COMPOSE_MODES = [
  { value: "post", label: "Post", icon: PenSquare },
  { value: "event", label: "Announce Event", icon: Swords },
  { value: "lfg", label: "Looking for Group", icon: Users },
];

// Post composer — shared by the "Post" and "Add Picture" rail buttons. `autoOpenPicker` makes
// the hidden file input auto-click the moment the dialog mounts, so "Add Picture" feels like its
// own dedicated action even though it's the same dialog underneath. Also doubles as the
// "Announce a Raid/Campaign/Game Show" composer (a host attaches one of their own hosted events,
// rendering a live RSVP banner — see KfPostCard's `event` block) AND the "Call to Arms" LFG
// composer (a structured Looking-for-Players/Looking-for-a-Table post — see `lfg` block).
export function KfComposeModal({ open, onOpenChange, autoOpenPicker, onPosted, initialText }) {
  const [mode, setMode] = useState("post");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [myEvents, setMyEvents] = useState({ raids: [], games: [], game_shows: [] });
  const [eventKind, setEventKind] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [lfgKind, setLfgKind] = useState(null);
  const [lfgSystem, setLfgSystem] = useState("any");
  const [lfgDay, setLfgDay] = useState("");
  const [lfgTime, setLfgTime] = useState("");
  const [lfgTableKind, setLfgTableKind] = useState(null); // "raid" | "game" — which real table a "Looking for Players" post recruits for
  const [lfgTableId, setLfgTableId] = useState(null);
  const [lfgPartySize, setLfgPartySize] = useState("10");
  const fileInputRef = useRef(null);

  const kindOptions = (kind) => (kind === "raid" ? myEvents.raids : kind === "game" ? myEvents.games : myEvents.game_shows);
  const hasAnyHostedEvent = myEvents.raids.length > 0 || myEvents.games.length > 0 || myEvents.game_shows.length > 0;
  const hasHostableTable = myEvents.raids.length > 0 || myEvents.games.length > 0;

  useEffect(() => {
    if (open) {
      setMode("post");
      setContent(initialText || "");
      setImageUrl(null);
      setEventKind(null);
      setEventId(null);
      setLfgKind(null);
      setLfgSystem("any");
      setLfgDay("");
      setLfgTime("");
      setLfgTableKind(null);
      setLfgTableId(null);
      setLfgPartySize("10");
      api.get("/kf/my-events").then(({ data }) => setMyEvents(data)).catch(() => {});
      if (autoOpenPicker) setTimeout(() => fileInputRef.current?.click(), 150);
    }
  }, [open, autoOpenPicker, initialText]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads?kind=kf_post_image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
    } catch {
      toast.error("Couldn't upload that picture — try again");
    } finally {
      setUploading(false);
    }
  };

  const pickEventKind = (kind) => {
    setEventKind((prev) => (prev === kind ? null : kind));
    setEventId(null);
  };

  const pickLfgTableKind = (kind) => {
    setLfgTableKind((prev) => (prev === kind ? null : kind));
    setLfgTableId(null);
  };

  const submit = async () => {
    if (mode === "event") {
      if (!eventKind || !eventId) return toast.error("Pick which one you're announcing");
    } else if (mode === "lfg") {
      if (!lfgKind) return toast.error("Pick Looking for Players or Looking for a Table");
      if (lfgKind === "looking_for_players" && (!lfgTableKind || !lfgTableId)) return toast.error("Pick which table you're recruiting for");
    } else if (!content.trim() && !imageUrl) {
      return toast.error("Write something or add a picture first");
    }
    setPosting(true);
    try {
      const payload = { content, image_url: imageUrl };
      if (mode === "event") {
        payload.post_type = "event_announcement";
        payload.event_kind = eventKind;
        payload.event_id = eventId;
      } else if (mode === "lfg") {
        payload.post_type = "lfg";
        payload.lfg_kind = lfgKind;
        payload.lfg_day = lfgDay;
        payload.lfg_time = lfgTime;
        if (lfgKind === "looking_for_players") {
          payload.event_kind = lfgTableKind;
          payload.event_id = lfgTableId;
          payload.lfg_party_size = lfgPartySize ? parseInt(lfgPartySize, 10) : null;
        } else {
          payload.lfg_system = lfgSystem;
        }
      }
      const { data } = await api.post("/kf/posts", payload);
      onPosted(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't post that — try again");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#322216] border-white/10 text-[#f1e0c5]" data-testid="kf-compose-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share with your fellow Knights</DialogTitle>
          <DialogDescription className="sr-only">Write a post, announce an event you're hosting, or post a Looking for Group listing</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 flex-wrap">
          {COMPOSE_MODES.filter((m) => m.value !== "event" || hasAnyHostedEvent).map((m) => {
            const Icon = m.icon;
            const active = mode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                data-testid={`kf-compose-mode-${m.value}`}
                onClick={() => setMode(m.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-wide transition-colors duration-150 border ${active ? "bg-[#D90429] text-white border-[#D90429]" : "border-white/15 text-[#f1e0c5]/60 hover:border-[#D90429]/50"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {m.label}
              </button>
            );
          })}
        </div>

        <Textarea
          data-testid="kf-compose-textarea"
          placeholder={mode === "lfg" ? "Any extra details for your fellow Knights?" : mode === "event" ? "Say something about it (optional)…" : "What victories or spoils of war do you have to share?"}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="bg-black/30 border-white/10 min-h-[100px]"
          maxLength={2000}
        />
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="kf-compose-file-input" />
        {imageUrl ? (
          <div className="relative rounded-lg overflow-hidden border border-white/10">
            <img src={imageUrl} alt="attachment" className="w-full max-h-64 object-cover" />
            <button data-testid="kf-compose-remove-image-button" onClick={() => setImageUrl(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center hover:bg-black/90">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <Button
            data-testid="kf-compose-add-picture-button"
            type="button"
            variant="secondary"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="rounded-full gap-2 w-fit"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Add a Picture"}
          </Button>
        )}

        {mode === "event" && (
          <div className="space-y-2.5 border-t border-white/10 pt-3">
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]/70">Which are you announcing?</p>
            <div className="flex gap-2">
              {EVENT_KINDS.filter((k) => kindOptions(k.value).length > 0).map((k) => {
                const Icon = k.icon;
                const active = eventKind === k.value;
                return (
                  <button
                    key={k.value}
                    type="button"
                    data-testid={`kf-compose-event-kind-${k.value}`}
                    onClick={() => pickEventKind(k.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-wide transition-colors duration-150 border ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-[#f1e0c5]/60 hover:border-[#D4AF37]/50"}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {k.label}
                  </button>
                );
              })}
            </div>
            {eventKind && (
              <Select value={eventId || ""} onValueChange={setEventId}>
                <SelectTrigger data-testid="kf-compose-event-select" className="bg-black/30 border-white/10">
                  <SelectValue placeholder={`Which ${eventKind === "raid" ? "Raid" : eventKind === "game" ? "Campaign" : "Game Show"}?`} />
                </SelectTrigger>
                <SelectContent>
                  {kindOptions(eventKind).map((item) => (
                    <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {mode === "lfg" && (
          <div className="space-y-2.5 border-t border-white/10 pt-3">
            <p className="text-[10px] uppercase tracking-widest text-[#3F9142]/80">What are you looking for?</p>
            <div className="flex gap-2">
              {LFG_KINDS.filter((k) => k.value !== "looking_for_players" || hasHostableTable).map((k) => {
                const active = lfgKind === k.value;
                return (
                  <button
                    key={k.value}
                    type="button"
                    data-testid={`kf-compose-lfg-kind-${k.value}`}
                    onClick={() => setLfgKind((prev) => (prev === k.value ? null : k.value))}
                    className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wide transition-colors duration-150 border ${active ? "bg-[#3F9142] text-white border-[#3F9142]" : "border-white/15 text-[#f1e0c5]/60 hover:border-[#3F9142]/50"}`}
                  >
                    {k.label}
                  </button>
                );
              })}
            </div>

            {lfgKind === "looking_for_players" ? (
              <>
                <p className="text-[10px] uppercase tracking-widest text-[#f1e0c5]/40">Which table is this for?</p>
                <div className="flex gap-2">
                  {[{ value: "raid", label: "Raid" }, { value: "game", label: "Campaign" }].filter((k) => kindOptions(k.value).length > 0).map((k) => {
                    const active = lfgTableKind === k.value;
                    return (
                      <button
                        key={k.value}
                        type="button"
                        data-testid={`kf-compose-lfg-table-kind-${k.value}`}
                        onClick={() => pickLfgTableKind(k.value)}
                        className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wide transition-colors duration-150 border ${active ? "bg-[#3F9142] text-white border-[#3F9142]" : "border-white/15 text-[#f1e0c5]/60 hover:border-[#3F9142]/50"}`}
                      >
                        {k.label}
                      </button>
                    );
                  })}
                </div>
                {lfgTableKind && (
                  <Select value={lfgTableId || ""} onValueChange={setLfgTableId}>
                    <SelectTrigger data-testid="kf-compose-lfg-table-select" className="bg-black/30 border-white/10">
                      <SelectValue placeholder={`Which ${lfgTableKind === "raid" ? "Raid" : "Campaign"}?`} />
                    </SelectTrigger>
                    <SelectContent>
                      {kindOptions(lfgTableKind).map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input
                  data-testid="kf-compose-lfg-party-size"
                  type="number" min="1" max="10"
                  placeholder="Full party size (default 10)"
                  value={lfgPartySize}
                  onChange={(e) => setLfgPartySize(e.target.value)}
                  className="bg-black/30 border-white/10"
                />
                <p className="text-[10px] text-[#f1e0c5]/35">We'll show which classes are already seated once players place their token — self-serve joining closes automatically at double the party size (so 20 for a regular table of 10), and the GM can always invite anyone directly beyond that.</p>
              </>
            ) : (
              <Select value={lfgSystem} onValueChange={setLfgSystem}>
                <SelectTrigger data-testid="kf-compose-lfg-system" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LFG_SYSTEMS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="kf-compose-lfg-day" placeholder="Day(s), e.g. Saturdays" value={lfgDay} onChange={(e) => setLfgDay(e.target.value)} className="bg-black/30 border-white/10" maxLength={40} />
              <Input data-testid="kf-compose-lfg-time" placeholder="Time, e.g. 7pm EST" value={lfgTime} onChange={(e) => setLfgTime(e.target.value)} className="bg-black/30 border-white/10" maxLength={40} />
            </div>
          </div>
        )}

        <Button data-testid="kf-compose-submit-button" onClick={submit} disabled={posting || uploading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
          {posting ? "Posting…" : "Post"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
